
import Vue from 'vue';
import {
  Crop16,
  Close16,
  WatsonHealthCircleMeasurement16,
  Stop16,
  StopFilledAlt16,
  CircleSolid16,
  CircleDash16,
  CenterToFit16,
} from '@carbon/icons-vue';
import {
  CvIconButton,
  CvButton,
  CvAccordion,
  CvAccordionItem,
  CvNumberInput,
} from '@carbon/vue';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { mapState, mapActions } from 'pinia';
import { ColorDropdown } from '../ColorManagement/ColorDropdown';
import { useStore as useColorsManagementStore } from '@/stores/v2/colorsManagement';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import ActionsPanel from './ActionsPanel.vue';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import { ProductPrintPositionAddModel } from '@/typings';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { updateTargetLines } from '@/utils/roundItemsHelper';
import { debounce } from 'throttle-debounce';
import AlignShapeControls from './shared/AlignShapeControls.vue';
import RotationShapeControls from './shared/RotationShapeControls.vue';
import { COLOR_ENGINE_V2_ACTIVE } from '@/constants/featureFlags';
import { isShapeInputSizeValid } from '@/utils/common';
import { LogoShape } from '../../typings/common';

export default Vue.extend({
  name: 'ImagePanelOptions',
  components: {
    ColorDropdown,
    ActionsPanel,
    Crop16,
    Close16,
    CvAccordion,
    CvAccordionItem,
    CvIconButton,
    CvButton,
    CvNumberInput,
    CircleSolid16,
    CircleDash16,
    CenterToFit16,
    AlignShapeControls,
    RotationShapeControls,
  },
  data() {
    return {
      showRoundPrintPositionOptions: false,
      hover: false,
      Crop16,
      WatsonHealthCircleMeasurement16,
      Stop16,
      StopFilledAlt16,
      CenterToFit16,
      debouncedClick: null as ((param1: any, param2: any) => void) | null,
      inputValidationState: {
        width: true,
        height: true,
      },
    };
  },

  computed: {
    ...mapState(useColorsManagementStore, ['availablePmsColors']),
    ...mapState(useShapeStore, ['shapes', 'activeShapeId', 'shapeById']),
    ...mapState(useProductStore, [
      'activePosition',
      'selectedPrintPosition',
      'selectedVariant',
      'rotation',
      'activePosition',
      'isFullColor',
      'unavailablePrintPositions',
      'panelsPositions',
      'centerCoordinatesOfPanels',
      'isAnyPositionAdded',
      'isSublimationTechnique',
    ]),
    isDuplicateDisabled(): boolean {
      const activeShape = this.shapes.find(
        (shape) => shape.id === this.activeShapeId
      );
      return (
        activeShape?.pngFileWithoutConverters === undefined ||
        activeShape?.pngFileWithoutConverters === null
      );
    },
    allPmsColors(): PmsColor[] {
      const detected = this.detectedPmsColors;

      const availableWithoutDetected = this.availablePmsColors.filter((c) => {
        return detected.findIndex((dc) => dc.name === c.name) === -1;
      });

      const availableDetected = this.availablePmsColors.filter((c) => {
        return detected.findIndex((dc) => dc.name === c.name) !== -1;
      });

      // show detected colors first, then the rest of available pms colors
      const mergedList = availableDetected.concat(availableWithoutDetected);

      return mergedList;
    },
    detectedPmsColors(): PmsColor[] {
      const array = this.activeShape?.pmsColorsInPngFile.map((color) => {
        return {
          ...color,
          name: color.name.includes('Closest Pantone: ')
            ? color.name.slice(17)
            : color.name,
        };
      });

      return array != null
        ? [...new Map(array.map((item) => [item['name'], item])).values()]
        : [];
    },
    activeShape(): LogoShape | undefined {
      var shape = this.shapes.find((shape) => shape.id === this.activeShapeId);
      if (shape != undefined && shape.kind === 'Logo') {
        return shape;
      }
      return undefined;
    },

    isRoundPosition(): boolean {
      return this.activePosition.printArea?.shapeType === 'Ellipse';
    },
    isRoundLogo(): boolean {
      if (this.activeShape) {
        return this.activeShape.isRoundLogo;
      }
      return false;
    },
  },

  watch: {
    activeShape() {
      this.inputValidationState.width = isShapeInputSizeValid(
        this.activeShape?.widthOnProductMM ?? 0,
        1
      );
      this.inputValidationState.height = isShapeInputSizeValid(
        this.activeShape?.heightOnProductMM ?? 0,
        1
      );
    },
  },

  created() {
    // Define the throttled function to handle clicks
    this.debouncedClick = debounce(1000, (param1: string, param2: number) => {
      this.onUpdate(param1, param2);
    });
  },

  methods: {
    ...mapActions(useShapeStore, [
      'removeShape',
      'deselectShape',
      'duplicateShape',
      'alignRoundItemShape',
      'transformShape',
      'makeLogoRound',
      'changeLogoBackground',
      'checkIsShapeInArea',
      'updateColorsAfterImageIsAdjusted',
      'applySublimationConverter',
      'applyEffectIfNeeded',
    ]),
    ...mapActions(useMirrorStore, ['removeMirrorShape', 'updateMirrorShape']),
    ...mapActions(useProductStore, [
      'getVariantLaserColor',
      'polygonDetails',
      'setCenterCoordinatesOfPanels',
      'isUncoloredConverterUsed',
    ]),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useGlobalStore, ['setIsLoaderActive']),

    onUpdateClick(param1: string, param2: number) {
      if (this.debouncedClick) {
        // Call the throttledClick method with the passed parameters
        this.debouncedClick(param1, param2);
      }
    },

    removeLogo() {
      const shape = this.shapes.find((s) => {
        return s.id === this.activeShapeId;
      });

      if (shape) {
        updateTargetLines(
          shape,
          this.centerCoordinatesOfPanels,
          this.panelsPositions,
          true
        );
      }
      this.removeMirrorShape(this.activeShapeId);
      this.removeShape(this.activeShapeId);
      this.runValidators();

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },

    async duplicateLogo() {
      await this.duplicateShape(this.activeShapeId);
      this.runValidators();

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },

    openLogoLibrary() {
      this.$emit('open-logo-library');
    },

    round(value: number, precision: number) {
      const multiplier = Math.pow(10, precision || 0);
      return Math.round(value * multiplier) / multiplier;
    },

    onUpdate(whatToUpdate: string, value: number) {
      if (whatToUpdate === 'width' || whatToUpdate === 'height') {
        const isValid = isShapeInputSizeValid(value, 1);
        this.inputValidationState[whatToUpdate] = isValid;

        if (!isValid) {
          return;
        }
      }

      this.transformShape(whatToUpdate, value);

      this.runValidators();

      const activeShape = this.shapeById(this.activeShapeId);
      if (this.isSublimationTechnique && activeShape && activeShape.isInArea) {
        this.applySublimationConverter(this.activeShapeId);
      }

      if (activeShape) {
        this.updateMirrorShape(activeShape);
      }

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },

    async replaceWith(index: number, color: string) {
      if (this.selectedPrintPosition.id) {
        this.setIsLoaderActive(true);
        if (this.detectedPmsColors[index].name === color) {
          // if the same color is selected, do nothing.
          return;
        }

        const selectedPmsColor =
          this.availablePmsColors?.find((c: PmsColor) => {
            return c.name === color;
          }) || undefined;

        if (selectedPmsColor) {
          const shapeStore = useShapeStore();
          if (COLOR_ENGINE_V2_ACTIVE) {
            await shapeStore.replaceColorV2(
              this.detectedPmsColors[index],
              selectedPmsColor,
              true
            );
          } else {
            await shapeStore.replaceColor(
              this.detectedPmsColors[index],
              selectedPmsColor,
              true
            );
          }

          const eventData = this.unavailablePrintPositions.filter(
            (u) => u.positionId === this.selectedPrintPosition.id
          ) as ProductPrintPositionAddModel[];

          const eventPayload = generateEventPayloadV2();

          this.$emit('wizard:price-params-changed', {
            productPrintPositions: eventPayload,
          });

          this.$emit('action:change-color', eventData);

          this.runValidators();
          const activeShape = this.shapeById(this.activeShapeId);
          if (activeShape) {
            this.updateMirrorShape(activeShape);
          }
          const snapshot = createStateSnapshot();
          this.recordHistory(snapshot);
          this.setIsLoaderActive(false);
        }
      }
    },

    async colorToRemove(color: PmsColor) {
      if (this.selectedPrintPosition.id) {
        this.setIsLoaderActive(true);
        const shapeStore = useShapeStore();

        if (COLOR_ENGINE_V2_ACTIVE) {
          await shapeStore.removeColorV2(color, true);
        } else {
          await shapeStore.removeColor(color, true);
        }

        const eventData = this.unavailablePrintPositions.filter(
          (u) => u.positionId === this.selectedPrintPosition.id
        ) as ProductPrintPositionAddModel[];

        this.$emit('action:change-color', eventData);

        const eventPayload = generateEventPayloadV2();

        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
        this.runValidators();

        const activeShape = this.shapeById(this.activeShapeId);
        if (activeShape) {
          this.updateMirrorShape(activeShape);
        }
        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);
        this.setIsLoaderActive(false);
      }
    },

    async roundLogo() {
      this.setIsLoaderActive(true);
      await this.makeLogoRound();
      await this.updateColorsAfterImageIsAdjusted(this.activeShapeId, false);

      const productStore = useProductStore();
      const activeShape = this.shapeById(this.activeShapeId);

      if (activeShape) {
        await this.applyEffectIfNeeded(
          productStore.selectedPrintPosition.id,
          productStore.selectedPrintPosition.techniqueId as string,
          activeShape
        );
        this.updateMirrorShape(activeShape);
      }

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });
      this.runValidators();

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
      this.setIsLoaderActive(false);
    },

    async removeWhiteFull() {
      this.setIsLoaderActive(true);
      await this.changeLogoBackground('fullBackground');
      this.runValidators();
      const activeShape = this.shapeById(this.activeShapeId);
      if (activeShape) {
        this.updateMirrorShape(activeShape);
      }

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
      this.setIsLoaderActive(false);
    },

    async removeWhiteOutside() {
      this.setIsLoaderActive(true);
      await this.changeLogoBackground('outsideBackground');
      this.runValidators();
      const activeShape = this.shapeById(this.activeShapeId);
      if (activeShape) {
        this.updateMirrorShape(activeShape);
      }

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
      this.setIsLoaderActive(false);
    },
  },
});
